import React, { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, FormControl, InputAdornment, InputLabel, MenuItem, Paper, Select, TextField, Tooltip, Typography
} from '@mui/material';
import { FileDownload as DownloadIcon, Search as SearchIcon } from '@mui/icons-material';
import { fmtInt } from '../../OnlineJournal/format';
import { NomenclatureMeta } from '../nomenclatureApi';
import { FILTER_LABELS, FilterKey, Filters, NONE, Option, countFilters } from '../nomenclatureCalc';
import { buttonSx } from '../nomenclatureStyles';

interface Props {
  meta: NomenclatureMeta;
  options: Record<FilterKey, Option[]>;
  /** Применённая строка поиска */
  term: string;
  filters: Filters;
  loading: boolean;
  /** Найдено по текущему отбору; null - список ещё не загружен */
  total: number | null;
  exporting: boolean;
  onSearch: (term: string) => void;
  onFilter: (patch: Partial<Filters>) => void;
  onReset: () => void;
  onExport: () => void;
}

const ANY = '*';

/** Ширина выпадающих списков: словари 1С длиннее перечислимых */
const WIDTHS: Record<FilterKey, number> = {
  type: 150, category: 130, priceGroup: 170, folder: 210, rxotc: 120, photo: 160, marked: 170, nkt: 170, ncels: 160
};
const ROW_1C: FilterKey[] = ['type', 'folder', 'category', 'priceGroup', 'rxotc'];
const ROW_SOURCES: FilterKey[] = ['nkt', 'ncels', 'photo', 'marked'];

/** Поиск по названию, МНН, артикулу, штрихкоду, производителю; фильтры применяются сразу */
const NomenclatureToolbar: React.FC<Props> = ({
  meta, options, term, filters, loading, total, exporting, onSearch, onFilter, onReset, onExport
}) => {
  const [draft, setDraft] = useState(term);
  // «Сброс» очищает и поле поиска
  useEffect(() => { setDraft(term); }, [term]);

  const value = draft.trim();
  const tooShort = value.length > 0 && value.length < meta.limits.termMin;
  const submit = () => {
    if (!tooShort) onSearch(value);
  };
  const active = countFilters(filters) + (term ? 1 : 0);

  const select = (key: FilterKey) => (
    <FormControl key={key} size="small" sx={{ width: WIDTHS[key] }}>
      <InputLabel shrink>{FILTER_LABELS[key]}</InputLabel>
      <Select
        notched displayEmpty label={FILTER_LABELS[key]} value={filters[key] || ANY}
        onChange={e => onFilter({ [key]: e.target.value === ANY ? '' : String(e.target.value) })}
        MenuProps={{ PaperProps: { sx: { maxHeight: 420 } } }}
      >
        <MenuItem value={ANY}>Все</MenuItem>
        {options[key].map(o => (
          <MenuItem key={o.value} value={o.value} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography sx={{ fontSize: '0.85rem', color: o.value === NONE || o.value === 'none' ? '#64748b' : undefined }}>{o.label}</Typography>
            {o.count !== undefined && <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{fmtInt(o.count)}</Typography>}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <TextField
          size="small" placeholder="Название, МНН, артикул, штрихкод или производитель" value={draft} autoFocus
          onChange={e => setDraft(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') submit(); }}
          inputProps={{ maxLength: 100 }} sx={{ minWidth: 260, flex: '1 1 340px', maxWidth: 520 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment>
          }}
        />

        <Tooltip title={tooShort ? `Введите не меньше ${meta.limits.termMin} символов` : ''} disableHoverListener={!tooShort}>
          <span>
            <Button
              variant="contained" size="small" onClick={submit} disabled={tooShort}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              sx={{ ...buttonSx, px: 2.5 }}
            >
              Найти
            </Button>
          </span>
        </Tooltip>

        <Button variant="outlined" color="inherit" size="small" onClick={onReset} disabled={!active} sx={{ ...buttonSx, borderColor: '#c4c4c4' }}>
          Сброс{active ? ` (${active})` : ''}
        </Button>

        <Box sx={{ flexGrow: 1 }} />

        {meta.permissions.export && (
          <Tooltip title={total ? `Список по текущему отбору в Excel (до ${fmtInt(meta.limits.export)} строк), без цен` : 'Нет товаров для выгрузки'}>
            <span>
              <Button
                variant="outlined" size="small" color="success" onClick={onExport} disabled={!total || exporting}
                startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />} sx={buttonSx}
              >
                Выгрузка
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        {ROW_1C.map(select)}
        <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: '#e2e8f0', mx: 0.5, display: { xs: 'none', lg: 'block' } }} />
        {ROW_SOURCES.map(select)}
      </Box>
    </Paper>
  );
};

export default React.memo(NomenclatureToolbar);
